import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { browserPushState, subscribeThisBrowser, type BrowserPushState } from "@ozpanel/kit";
import { useAuth, useToast } from "@ozpanel/ui";

// ONE "enable push on this browser" for the admin console — the Notifications page and the shell's
// banner both use it, so the two can never subscribe differently. Registers the console's own worker
// (sw.js under the console's base path), subscribes with the server's VAPID key and stores the
// subscription for the signed-in admin.
export function usePushEnable() {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [state, setState] = React.useState<BrowserPushState>(browserPushState);
  const [busy, setBusy] = React.useState(false);

  const enable = React.useCallback(async () => {
    setBusy(true);
    try {
      const reached = await subscribeThisBrowser({
        baseURL: new URL(import.meta.env.BASE_URL, window.location.origin).toString(),
        vapidKey: async () => (await client.apiFetch<{ public_key: string }>("/notify/push/vapid-key")).public_key,
        save: async (endpoint, keys, userAgent) => {
          await client.apiFetch("/notify/push/subscribe", {
            method: "POST",
            body: JSON.stringify({ endpoint, keys, user_agent: userAgent }),
          });
        },
      });
      setState(reached);
      if (reached === "granted") {
        notify({ kind: "success", title: "Alerts will be pushed to this browser" });
        void qc.invalidateQueries({ queryKey: ["notify-push-devices"] });
        void qc.invalidateQueries({ queryKey: ["notify-push-status"] });
      } else if (reached === "denied") {
        notify({
          kind: "error",
          title: "This browser blocked notifications",
          body: "It can only be changed in the browser's own site settings for this page.",
        });
      }
    } catch (e) {
      notify({ kind: "error", title: "Could not subscribe this browser", body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }, [client, notify, qc]);

  return { state, busy, enable };
}
